/**
 * Light or dark, and which one this person has asked for.
 *
 * **The stylesheet never reads `prefers-color-scheme`.** This module resolves the
 * preference and stamps `data-theme` on the root element; every token block keys off that
 * attribute. Both arrangements work on their own, but *system* has to re-resolve when the
 * operating system flips at sunset, which means a `matchMedia` listener exists either way —
 * and given that listener, a media query in the CSS is a second opinion that will eventually
 * disagree with the first. One authority, and it is this file.
 *
 * The preference is cached in `localStorage`, in the same place and shape as the language
 * i18next keeps, because the sign-in page needs a theme before anybody has signed in and
 * `index.html` stamps it before the first paint. The person's own choice lives on their
 * membership and is written here at sign-in (`PreferencesProvider`), so what this key
 * holds is the last person's choice — right for them, and overwritten by the next.
 *
 * The first stamp is **not** made here. It is made by an inline script in `index.html`,
 * before first paint — see the comment there. This module takes over once the bundle has
 * parsed, and agrees with it because both read the same key by the same rules.
 */

import { inkOn, readableOn, type Preference, type Scheme } from '@taxi/ui/colour';

export type { Preference, Scheme };

const KEY = 'taxi.theme';

/**
 * The grounds, for the moment before the stylesheet can be asked.
 *
 * Normally {@link ground} reads `--ground` off the document, so the CSS stays the single
 * source of truth for what a scheme looks like. These are the answer when that read comes
 * back empty — a test environment with no stylesheet attached, mostly.
 */
const FALLBACK_GROUND: Record<Scheme, string> = { dark: '#0e1318', light: '#f4f6f8' };

const listeners = new Set<(scheme: Scheme) => void>();

/** The OS's own answer, watched so that *system* follows it rather than freezing at load. */
const systemPrefersDark = window.matchMedia('(prefers-color-scheme: dark)');

function isPreference(value: string | null): value is Preference {
  return value === 'system' || value === 'light' || value === 'dark';
}

/** What this browser has been told to do. `system` when nobody has said. */
export function preference(): Preference {
  try {
    const stored = localStorage.getItem(KEY);

    return isPreference(stored) ? stored : 'system';
  } catch {
    // A browser with storage disabled is a browser that follows the system, which is the
    // default anyway. Not being able to remember a choice is not a reason to fail to make
    // one.
    return 'system';
  }
}

/** The scheme actually being drawn, preference and operating system taken together. */
export function scheme(): Scheme {
  const chosen = preference();

  if (chosen !== 'system') return chosen;

  return systemPrefersDark.matches ? 'dark' : 'light';
}

/**
 * Stamps the attribute and tells everything that draws outside CSS.
 *
 * The map is the reason the listeners exist: MapLibre paint properties and the marker
 * icons are canvas and GPU state rather than styled DOM, so nothing about them changes
 * when a custom property does.
 */
function apply(): void {
  const active = scheme();

  document.documentElement.setAttribute('data-theme', active);

  for (const listener of listeners) listener(active);
}

/** Remembers a choice and applies it now. */
export function choose(next: Preference): void {
  try {
    localStorage.setItem(KEY, next);
  } catch {
    // Applied anyway. A dispatcher who cannot persist the choice should still get it for
    // this session rather than watching a control that appears not to work.
  }

  apply();
}

/**
 * Runs a function whenever the drawn scheme changes, and returns the way to stop.
 *
 * Called with the current scheme immediately, so a subscriber has one code path rather
 * than one for "set it up" and another for "it changed" — which is where the two drift.
 */
export function onSchemeChange(listener: (active: Scheme) => void): () => void {
  listeners.add(listener);
  listener(scheme());

  return () => listeners.delete(listener);
}

/**
 * The value of a custom property, as the stylesheet currently defines it.
 *
 * What lets the map be themed without a second palette in TypeScript. A layer's paint
 * property is not CSS and cannot reference a variable, so it asks for the resolved value at
 * the moment it is set — and the stylesheet stays the one place a colour is written down.
 */
export function token(name: string, fallback = '#000000'): string {
  const value = getComputedStyle(document.documentElement).getPropertyValue(name).trim();

  return value.length > 0 ? value : fallback;
}

/** The ground the active scheme draws on. */
function ground(): string {
  return token('--ground', FALLBACK_GROUND[scheme()]);
}

/**
 * Stamps a firm's accent, corrected for the ground it is about to be drawn on.
 *
 * A firm whose accent is legible on black and invisible on white is the case this exists
 * for. `readableOn` is idempotent, so re-running it on every scheme change — which is what
 * has to happen, since the ground has moved — corrects the firm's original colour rather
 * than walking away from it a step at a time.
 *
 * `--on-brand` goes with it: the ink for a label sitting *inside* a brand-filled button is a
 * property of the fill, not of the scheme, and the console wrote `#fff` there for a year.
 */
export function applyBrand(primary: string): void {
  const accent = readableOn(primary, ground());

  document.documentElement.style.setProperty('--brand-primary', accent);
  document.documentElement.style.setProperty('--on-brand', inkOn(accent));
}

// The OS flipping at sunset, for everybody who has not overridden it. Registered at module
// load rather than from a component, because there is exactly one of these and a component
// that unmounted would take it with it.
systemPrefersDark.addEventListener('change', () => {
  if (preference() === 'system') apply();
});

// A second tab on the same workstation. Storage events fire only in the *other* documents,
// which is precisely the case that would otherwise leave two consoles disagreeing until one
// of them was reloaded.
window.addEventListener('storage', (event) => {
  if (event.key === KEY) apply();
});
